import React from 'react';
import PropTypes from 'prop-types';

// =============================================================================
// StatusPill — thin React wrapper around the `.status-pill` CSS primitive.
//
// Owns the DOM shape (span with optional dot + label) and the legacy class
// passthrough. Does NOT own any status-to-tone mapping — callers decide the
// tone + label. For Campaign status semantics see `ux/Campaigns/shared/
// StatusTag.js` (maps DRAFT / LIVE / etc. → tone + label via STATUS_PILL).
//
// Tokens: tones emerald / primary / amber / sky / neutral / critical.
// Full contract: SPECIFICATION.md §10 "`.status-pill` consumer contract".
// =============================================================================

const StatusPill = ({ tone, label, dot = true, status, className }) => {
  // Legacy compatibility: `CampaignsTable.scss` scopes overrides under
  // `.campaigns-table-wrapper .status-badge-{STATUS}` — preserve those
  // classes when a `status` prop is passed so the table-local palette
  // still renders inside that wrapper. Outside it (e.g. Campaign Details
  // header) only `.status-pill--{tone}` applies.
  const legacyClasses = status
    ? ` status-badge-enhanced status-badge-${status}`
    : '';
  const extraClass = className ? ` ${className}` : '';

  return (
    <span
      className={`status-pill status-pill--${tone}${legacyClasses}${extraClass}`}
      role="status"
      aria-label={typeof label === 'string' ? `Status: ${label}` : undefined}
    >
      {dot && <span className="status-pill__dot" aria-hidden="true" />}
      {label}
    </span>
  );
};

StatusPill.propTypes = {
  tone: PropTypes.oneOf(['emerald', 'primary', 'amber', 'sky', 'neutral', 'critical'])
    .isRequired,
  label: PropTypes.node.isRequired,
  dot: PropTypes.bool,
  status: PropTypes.string,
  className: PropTypes.string,
};

export default StatusPill;
